import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { apiPost, isInvalidSessionError } from "../api";
import { useAuth } from "../auth/AuthProvider";
import BaseButton from "../components/BaseButton/BaseButton";
import ExternalButton from "../components/BaseButton/ExternalButton";
import LinkButton from "../components/BaseButton/LinkButton";
import PageHeader from "../components/PageHeader";
import { useAppData } from "../data/AppDataProvider";
import { debugHistory } from "../navigation/debug";
import { readBaseHistoryIndex, readHistoryIndex } from "../navigation/history";

const PAYMENT_ORDER_STORAGE_KEY = "payment_order";
const EMPTY_PAYMENT = null;
const PAYMENT_BACK_EVENT = "app:payment-back";
const PAYMENT_GUARD_KEY = "paymentGuard";
const PAYMENT_STATUS_POLL_INTERVAL_MS = 10000;
const TERMINAL_ORDER_STATUSES = new Set([
    "completed",
    "cancelled_by_user",
    "cancelled_by_timer",
]);

function loadStoredPayment() {
    const rawValue = localStorage.getItem(PAYMENT_ORDER_STORAGE_KEY);
    if (!rawValue) {
        return EMPTY_PAYMENT;
    }

    try {
        return JSON.parse(rawValue);
    } catch {
        localStorage.removeItem(PAYMENT_ORDER_STORAGE_KEY);
        return EMPTY_PAYMENT;
    }
}

function getPaytypeState(payment) {
    if (!payment) {
        return {};
    }

    return {
        devices: Number(payment.devices) || 1,
        monthsCount: Number(payment.monthsCount) || 0,
        monthsKey: payment.monthsKey || "",
        monthsLabel: payment.monthsLabel || "",
        price: Number(payment.price) || 0,
    };
}

function parseRequisites(payment) {
    const rec = String(payment?.rec || "");
    const parts = rec.split(";");

    if (payment?.valute === "rub" && parts.length >= 3) {
        return {
            requisite: parts[0],
            fio: parts[1],
            bank: parts[2],
            requisiteLabel: payment?.paymentType === "sbp" ? "Номер телефона" : "Номер карты",
        };
    }

    return {
        requisite: rec,
        fio: "",
        bank: "",
        requisiteLabel: "Кошелек",
    };
}

function formatTimer(seconds) {
    const safeSeconds = Math.max(0, seconds);
    const minutes = String(Math.floor(safeSeconds / 60)).padStart(2, "0");
    const secs = String(safeSeconds % 60).padStart(2, "0");
    return `${minutes}:${secs}`;
}

function formatAmount(payment) {
    const amountValue = payment?.amount || "";
    const amountRub = payment?.amount_rub || "";
    const valute = String(payment?.valute || "").toUpperCase();

    if (payment?.valute === "rub") {
        return `${amountRub} ₽`;
    }

    return `${amountValue} ${valute}`.trim();
}

function openCancelModal(setModalVisible) {
    setModalVisible(true);
}

function canReturnToPaytype() {
    return readHistoryIndex() - readBaseHistoryIndex() >= 3;
}

function getResultState(orderStatus) {
    if (orderStatus === "completed") {
        return {
            kind: "success",
            title: "Платеж принят",
            description: "Оплата прошла успешно. Перейдите на страницу с инструкцией, чтобы начать пользоваться VPN",
        };
    }

    if (orderStatus && orderStatus !== "pending") {
        return {
            kind: "cancelled",
            title: "Оплата отменена",
            description: "Время отведенное на оплату истекло. Вы можете совершить платеж заново.",
        };
    }

    return null;
}

function Payment() {
    const auth = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { refreshMainPageData, refreshCabinetData, refreshDevicesData, refreshTariffsData } = useAppData();
    const [payment, setPayment] = useState(() => location.state || loadStoredPayment());
    const [timeLeft, setTimeLeft] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState("");
    const [orderStatus, setOrderStatus] = useState(() => payment?.status || "pending");
    const [resultState, setResultState] = useState(() => getResultState(payment?.status));
    const getAppearStyle = () => ({ "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s` });

    useEffect(() => {
        if (location.state?.order_id) {
            debugHistory("Payment", "effect:location-state", {
                orderId: location.state.order_id,
                locationState: location.state,
            });
            localStorage.setItem(PAYMENT_ORDER_STORAGE_KEY, JSON.stringify(location.state));
            setPayment(location.state);
        }
    }, [location.state]);

    useEffect(() => {
        setOrderStatus(payment?.status || "pending");
        setResultState(getResultState(payment?.status));
    }, [payment]);

    useEffect(() => {
        if (!payment?.order_id || TERMINAL_ORDER_STATUSES.has(orderStatus)) {
            return;
        }

        const tick = () => {
            const deadlineAt = Number(payment.deadlineAt) || 0;
            const nextTimeLeft = Math.max(0, Math.floor((deadlineAt - Date.now()) / 1000));
            setTimeLeft(nextTimeLeft);
        };

        tick();
        const timerId = window.setInterval(tick, 1000);
        return () => window.clearInterval(timerId);
    }, [orderStatus, payment]);

    useEffect(() => {
        if (!payment?.order_id || location.pathname !== "/payment" || TERMINAL_ORDER_STATUSES.has(orderStatus)) {
            return undefined;
        }

        let isActive = true;

        const syncOrderStatus = async () => {
            try {
                const response = await apiPost(
                    "/cabinet/order_status",
                    { order_id: payment.order_id },
                    auth,
                );
                if (!isActive) {
                    return;
                }

                const nextStatus = String(response.status || "pending");
                setOrderStatus(nextStatus);

                if (!TERMINAL_ORDER_STATUSES.has(nextStatus)) {
                    return;
                }

                const nextPayment = {
                    ...payment,
                    status: nextStatus,
                };

                if (nextStatus === "completed") {
                    localStorage.removeItem(PAYMENT_ORDER_STORAGE_KEY);
                } else {
                    localStorage.setItem(PAYMENT_ORDER_STORAGE_KEY, JSON.stringify(nextPayment));
                }

                setPayment(nextPayment);
                setResultState(getResultState(nextStatus));

                if (nextStatus === "completed") {
                    void refreshMainPageData();
                    void refreshCabinetData();
                    void refreshDevicesData();
                    void refreshTariffsData();
                }
            } catch (error) {
                if (!isActive) {
                    return;
                }

                if (isInvalidSessionError(error)) {
                    auth.logout();
                }
            }
        };

        void syncOrderStatus();
        const intervalId = window.setInterval(() => {
            void syncOrderStatus();
        }, PAYMENT_STATUS_POLL_INTERVAL_MS);

        return () => {
            isActive = false;
            window.clearInterval(intervalId);
        };
    }, [
        auth,
        location.pathname,
        orderStatus,
        payment,
        refreshCabinetData,
        refreshDevicesData,
        refreshMainPageData,
        refreshTariffsData,
    ]);

    useEffect(() => {
        if (!payment?.order_id || location.pathname !== "/payment" || resultState) {
            return undefined;
        }

        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        debugHistory("Payment", "effect:guard-start", {
            orderId: payment.order_id,
            currentUrl,
            baseIdx: readBaseHistoryIndex(),
            currentIdx: readHistoryIndex(),
            canReturnToPaytype: canReturnToPaytype(),
        });

        const ensurePaymentGuard = () => {
            if (window.history.state?.[PAYMENT_GUARD_KEY]) {
                debugHistory("Payment", "guard:skip-existing", {
                    orderId: payment.order_id,
                });
                return;
            }

            debugHistory("Payment", "guard:push-before", {
                orderId: payment.order_id,
                currentUrl,
            });
            window.history.pushState(
                {
                    ...window.history.state,
                    [PAYMENT_GUARD_KEY]: true,
                },
                "",
                currentUrl,
            );
            debugHistory("Payment", "guard:push-after", {
                orderId: payment.order_id,
            });
        };

        const handlePopState = (event) => {
            debugHistory("Payment", "popstate", {
                orderId: payment.order_id,
                eventState: event.state ?? null,
            });
            openCancelModal(setModalVisible);
            ensurePaymentGuard();
        };

        const handlePaymentBack = () => {
            debugHistory("Payment", "telegram-back-event", {
                orderId: payment.order_id,
            });
            openCancelModal(setModalVisible);
        };

        ensurePaymentGuard();
        window.addEventListener("popstate", handlePopState);
        window.addEventListener(PAYMENT_BACK_EVENT, handlePaymentBack);

        return () => {
            debugHistory("Payment", "effect:guard-cleanup", {
                orderId: payment.order_id,
            });
            window.removeEventListener("popstate", handlePopState);
            window.removeEventListener(PAYMENT_BACK_EVENT, handlePaymentBack);
        };
    }, [location.pathname, payment?.order_id, resultState]);

    const paymentDetails = useMemo(() => parseRequisites(payment), [payment]);

    function copy(text) {
        if (!text || resultState) {
            return;
        }
        navigator.clipboard.writeText(String(text));
    }

    async function cancelPayment() {
        if (!payment?.order_id || isCancelling || resultState) {
            return;
        }

        debugHistory("Payment", "cancel:start", {
            orderId: payment.order_id,
            canReturnToPaytype: canReturnToPaytype(),
            baseIdx: readBaseHistoryIndex(),
            currentIdx: readHistoryIndex(),
        });
        setIsCancelling(true);
        setError("");

        try {
            await apiPost(
                "/cabinet/cancel_order",
                { order_id: payment.order_id },
                auth,
            );

            localStorage.removeItem(PAYMENT_ORDER_STORAGE_KEY);
            setModalVisible(false);
            debugHistory("Payment", "cancel:success-before-navigation", {
                orderId: payment.order_id,
                canReturnToPaytype: canReturnToPaytype(),
                baseIdx: readBaseHistoryIndex(),
                currentIdx: readHistoryIndex(),
                fromPaytypeFlow: Boolean(payment.fromPaytypeFlow),
            });
            if (payment.fromPaytypeFlow) {
                debugHistory("Payment", "cancel:navigate-history-go", {
                    orderId: payment.order_id,
                    delta: -2,
                });
                window.history.go(-2);
                return;
            }

            debugHistory("Payment", "cancel:navigate-paytype-replace", {
                orderId: payment.order_id,
                paytypeState: getPaytypeState(payment),
            });
            navigate("/paytype", { replace: true, state: getPaytypeState(payment) });
        } catch (error) {
            if (isInvalidSessionError(error)) {
                auth.logout();
                return;
            }

            setError("Не удалось отменить заказ");
        } finally {
            setIsCancelling(false);
        }
    }

    if (!payment?.order_id) {
        return (
            <>
                <PageHeader title="Оплата" className="payment-header" />
                <div className="attention-block pad1 menu-appear" style={getAppearStyle()}>
                    Данные платежа не найдены. Вернитесь назад и создайте новый ордер.
                </div>
                <LinkButton to="/tariffs" className="menu-appear" style={getAppearStyle()}>
                    <span>Перейти к тарифам</span>
                </LinkButton>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title={`Оплата #${payment.order_id}`}
                className="payment-header"
                showHomeButton={false}
                rightSlot={<div id="timer" className="payment-timer">{formatTimer(timeLeft)}</div>}
            />

            <div className="flex-col gap05 menu-appear" style={getAppearStyle()}>
                <span>
                    Совершите перевод на указанные реквизиты. Платеж будет зачислен автоматически.
                </span>
                {error ? <div className="attention-block pad1 menu-appear" style={getAppearStyle()}>{error}</div> : null}
            </div>

            <div className="gray-block pad1 shadow-1 flex-col gap05 menu-appear" style={getAppearStyle()}>
                <p className="w100"><b>Реквизиты для перевода</b></p>

                {payment.valute === "rub" && paymentDetails.bank ? (
                    <div className="attention-block" style={{ padding: "8px 10px 10px 10px", marginBottom: 0 }}>
                        Внимание! Переводить деньги нужно строго в указанный банк.
                    </div>
                ) : null}

                {paymentDetails.bank ? (
                    <div className="flex-col w100">
                        <div className="flex-start align-center w100">
                            <span>┌ Банк получателя:</span>
                        </div>
                        <div className="flex-start align-center w100">
                            <span style={{ marginRight: "4px" }}>└</span>
                            <span className="span-summ" style={{ marginLeft: 0, fontSize: "1em", fontWeight: "bold" }}>
                                {paymentDetails.bank}
                            </span>
                        </div>
                    </div>
                ) : null}

                {paymentDetails.fio ? (
                    <div className="flex-col w100">
                        <div className="flex-start align-center w100">
                            <span>┌ Получатель:</span>
                        </div>
                        <div className="flex-start align-center w100">
                            <span style={{ marginRight: "4px" }}>└</span>
                            <span className="span-summ" style={{ marginLeft: 0, fontSize: "1em", fontWeight: "bold" }}>
                                {paymentDetails.fio}
                            </span>
                        </div>
                    </div>
                ) : null}

                <div className="flex-col ssilka-block">
                    {paymentDetails.requisiteLabel}:
                    <span
                        className="ssilka flex-between align-center gap05 w100"
                        onClick={() => copy(paymentDetails.requisite)}
                        style={{ cursor: resultState ? "default" : "pointer" }}
                    >
                        <span className="font09"
                        style={{ wordBreak: "break-word" }}
                        >{paymentDetails.requisite}</span>
                        <svg className="svg-btns">
                            <use xlinkHref="#icon-rect"></use>
                        </svg>
                    </span>
                    <span className="ukazatel">
                        Нажмите, чтобы скопировать
                    </span>
                </div>

                <div className="flex-col ssilka-block">
                    Сумма:
                    <span
                        className="ssilka flex-between align-center gap05 w100"
                        onClick={() => copy(formatAmount(payment))}
                        style={{ cursor: resultState ? "default" : "pointer" }}
                    >
                        <span className="font09">{formatAmount(payment)}</span>
                        <svg className="svg-btns">
                            <use xlinkHref="#icon-rect"></use>
                        </svg>
                    </span>
                    <span className="ukazatel">
                        Нажмите, чтобы скопировать
                    </span>
                </div>
            </div>

            <div className="flex gap1 flex-between menu-appear" style={getAppearStyle()}>
                <button
                    className="btn-color font09 btn-red h100 flex align-center justify-center shadow-1"
                    style={{ height: "100%", flex: 1 }}
                    onClick={() => openCancelModal(setModalVisible)}
                    disabled={Boolean(resultState)}
                >
                    Отменить
                </button>
                <ExternalButton
                    icon="icon-support"
                    href="https://t.me/sodavpn_support"
                    className="bnt-devices justify-center"
                    style={{ flex: 1 }}
                    disabled={Boolean(resultState)}
                >
                    <span>Поддержка</span>
                </ExternalButton>
            </div>

            <div className={`modal-overlay modal-overlay-payment-cancel ${modalVisible ? "active" : ""}`}>
                <div className="modal-window payment-cancel-modal flex-col gap05 pad1">
                    <h2 className="font11 modal-title">
                        Отменить платеж?
                    </h2>
                    <p className="font08">
                        Вы сможете выбрать другой способ оплаты или оплатить позже
                    </p>
                    <div className="flex-between gap1">
                        <button
                            className="modal-btn btn-red confirm"
                            style={{ flex: 1 }}
                            onClick={cancelPayment}
                            disabled={isCancelling || Boolean(resultState)}
                        >
                            <span>{isCancelling ? "Отмена..." : "Отменить"}</span>
                        </button>
                        <button
                            className="modal-btn cancel"
                            style={{ flex: 1 }}
                            onClick={() => setModalVisible(false)}
                            disabled={isCancelling}
                        >
                            <span>Назад</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className={`payment-result-overlay ${resultState ? "active" : ""}`}>
                <div className={`payment-result-card payment-result-card-${resultState?.kind || "default"} menu-appear`} style={getAppearStyle()}>
                    <div className={`payment-result-icon payment-result-icon-${resultState?.kind || "default"}`}>
                        {resultState?.kind === "success" ? (
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M9.55 16.6 5.4 12.45l1.4-1.4 2.75 2.75 7.65-7.65 1.4 1.4Z"
                                    fill="currentColor"
                                />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="m13.4 12 4.9-4.9-1.4-1.4-4.9 4.9-4.9-4.9-1.4 1.4 4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4Z"
                                    fill="currentColor"
                                />
                            </svg>
                        )}
                    </div>

                    <div className="payment-result-text">
                        <h2>{resultState?.title}</h2>
                        {resultState?.description ? <p>{resultState.description}</p> : null}
                    </div>

                    <BaseButton
                        onClick={() => navigate("/", { replace: true })}
                        className="justify-center menu-appear"
                        style={{ ...getAppearStyle(), width: "100%" }}
                    >
                        <span>Вернуться в главное меню</span>
                    </BaseButton>
                </div>
            </div>
        </>
    );
}

export default Payment;
